import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import { Pencil } from 'lucide-react';
import * as React from 'react';

import { SPLIT_HOLD } from './open-beside';

/** What the shell hands the control it draws while editing. */
export interface IInlineControl<D> {
  /** The control's accessible name — "<label> for <name>". */
  label: string;
  value: D;
  onChange: (next: D) => void;
  /** True while the write is out. */
  disabled: boolean;
}

type InlineState = 'reading' | 'editing' | 'saving';

/**
 * What `onSave` / `onClear` may answer besides `null` (landed) and a sentence (refused): **back to
 * editing, nothing said** — a write the domain asked a question about first (a reason, say) and the
 * person declined to answer. The draft is kept and focus returns to the control, as after a
 * refusal, but no alert is drawn, because nothing was refused (SR-222's F4, R6).
 */
export const KEEP_EDITING: unique symbol = Symbol('InlineField.keepEditing');

/** What a save answers: `null` when it landed, a sentence when refused, or `KEEP_EDITING`. */
export type InlineAnswer = Nullable<string> | typeof KEEP_EDITING;

/**
 * One value of a record, read in place and edited in place — a pencil per field (the advisor record
 * page's roadmap, K2 and its ruling U2; the F1 kit plan's R5, which this carries whole).
 *
 * **Three states.** The pencil opens `editing` with a draft started from `value`; Save goes to
 * `saving`, and back to `reading` when the write landed — or to `editing` with the refusal said
 * beside the field and the draft kept, so the next Save sends it again. Several fields may be open
 * at once, and each saves alone: one write per field on the wire.
 *
 * **Words in, never an error** (R4): `onSave` and `onClear` resolve to `null` when the write
 * landed — after the page shows the new value, so the field does not close over the old one — or
 * to the sentence to say — or to `KEEP_EDITING`, back to editing with nothing said, when the domain
 * asked a question before writing and the answer was no. The domain makes the sentence, and
 * decides what the draft means: a value saved unchanged, which sends nothing and answers `null`,
 * and a value no payload can carry, held with the shape the field wants as its answer (the
 * runtime's `isMalformed` rule, not validation).
 * **An emptied control is a clear**, sent through `onClear` (a `DELETE` on the wire) — and clearing
 * a value already empty sends nothing. A control that cannot be empty (a tick, whose unticked is
 * the value `false`) gets no `onClear`.
 *
 * **Keys.** Enter saves from a one-line input; Ctrl/⌘+Enter from anywhere in the field — a text
 * area, the rich-text editor, a choice — and is taken before the control sees it, so a list does
 * not open and the editor adds no line break. Esc cancels from anywhere in the field; a key pressed
 * in a menu or list drawn outside it (a select's options, the editor's link box) is that menu's.
 * **While editing or saving, the field carries `SPLIT_HOLD`**: the People split view's Esc would
 * otherwise close the record over the draft whenever focus sits on Save, Cancel or a tick, none of
 * which is a field to it.
 *
 * **Focus** moves into the control on edit — waiting for one that loads later, as the editor does —
 * and back to the pencil on leaving it.
 *
 * **Read-only is a state of its own**: no pencil, and the reason in visible text under the value
 * ("Derived from the registry lookups"). A tooltip alone would vanish from screenshots and specs.
 *
 * **Not permitted is not read-only** (SR-228's R3): the field is editable, by somebody else's
 * roles. The pencil is drawn **disabled**, described by a sentence the caller draws once for every
 * field it holds back (`NotPermitted`, its id handed in as `notPermitted`) — one sentence per
 * table, not one per cell — and the value's `actions`, its history, stay.
 *
 * Nothing here checks a value: no length, no bound, no format. survey-api judges, and its reasons
 * come back as the sentence.
 *
 * Hooks: `[data-field]` (the field's key), `[data-state]`, `[data-pending]` while saving and
 * `[data-readonly]` — fixed by the F1 plan for the consumers' specs.
 */
export function InlineField<D>({
  label,
  name,
  fieldKey,
  display,
  value,
  readOnly,
  notPermitted,
  renderControl,
  isEmpty,
  onSave,
  onClear,
  onStart,
  onOpenChange,
  multiline,
  notice,
  actions
}: {
  /** The field's label, as drawn above it. */
  label: string;
  /** Whose value it is — the record's name, so a household's fields are told apart. */
  name: string;
  fieldKey: string;
  /** The value as read. */
  display: React.ReactNode;
  /** The value as a draft — what an edit starts from. */
  value: D;
  readOnly?: { reason: string };
  /** The id of the `NotPermitted` sentence saying why the pencil is off; absent, it is on. */
  notPermitted?: string;
  renderControl: (control: IInlineControl<D>) => React.ReactNode;
  isEmpty: (draft: D) => boolean;
  onSave: (draft: D) => Promise<InlineAnswer>;
  onClear?: () => Promise<InlineAnswer>;
  /** Called as the pencil opens the field — so a domain can note what the draft started from, when
   *  `value` may move under an open draft (a page re-read by another field's write). */
  onStart?: () => void;
  /** Told as the field opens (editing, then saving) and as it closes — an unmount included — so a
   *  domain whose page can take the field away under an open draft (a condition resolved off by
   *  another field's write) can hold it until the person has closed it. */
  onOpenChange?: (open: boolean) => void;
  /** Plain Enter belongs to the control (a new line, a tick) — only Ctrl/⌘+Enter saves. */
  multiline?: boolean;
  /** Said above Save while editing — what saving would drop, say. */
  notice?: React.ReactNode;
  /** Drawn beside the pencil while reading — the value's history. */
  actions?: React.ReactNode;
}) {
  const [state, setState] = React.useState<InlineState>('reading');
  const [draft, setDraft] = React.useState<D>(value);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const group = React.useRef<HTMLDivElement>(null);
  const control = React.useRef<HTMLDivElement>(null);
  const pencil = React.useRef<HTMLButtonElement>(null);
  const named = `${label} for ${name}`;

  const editing = state === 'editing';
  const saving = state === 'saving';

  const openChanged = React.useRef(onOpenChange);
  openChanged.current = onOpenChange;
  const open = state !== 'reading';
  React.useEffect(() => {
    if (!open) return undefined;
    openChanged.current?.(true);
    return () => openChanged.current?.(false);
  }, [open]);

  const start = () => {
    onStart?.();
    setDraft(value);
    setRefusal(null);
    setState('editing');
  };

  const cancel = () => {
    setRefusal(null);
    setState('reading');
  };

  const save = async () => {
    if (!editing) return;
    let write: () => Promise<InlineAnswer>;
    if (!isEmpty(draft) || !onClear) write = () => onSave(draft);
    else if (isEmpty(value)) return cancel();
    else write = onClear;

    setState('saving');
    setRefusal(null);
    let said: InlineAnswer;
    try {
      said = await write();
    } catch (error) {
      // The contract is a sentence or null; a callback that throws broke it, and whether the write
      // landed is unknown — so the field says that much rather than nothing, and logs the break.
      console.error('[InlineField] the save callback threw instead of answering:', error);
      said = 'No answer could be read — check whether it was saved before trying again.';
    }
    if (said === null) {
      setState('reading');
    } else if (said === KEEP_EDITING) {
      setState('editing');
    } else {
      setRefusal(said);
      setState('editing');
    }
  };

  // Focus follows the state: into the control on edit, back to the pencil on leaving it. The
  // first render is not a transition, so a page opening moves no focus. A refusal brings it back
  // into the control too, and so does `KEEP_EDITING`: the control and Save were disabled while
  // the write was out, which drops focus to the page, and the draft is what the next keystroke is
  // for. But an answer arrives whenever it arrives, and several fields may be open: focus is
  // moved only from inside this field or from nowhere, never out of another field the person has
  // gone on to type in.
  const previous = React.useRef<InlineState>(state);
  React.useEffect(() => {
    const was = previous.current;
    previous.current = state;
    const active = document.activeElement;
    const ours =
      active === null || active === document.body || (group.current?.contains(active) ?? false);
    if (state === 'editing' && (was === 'reading' || ours) && control.current) {
      return focusWhenDrawn(control.current);
    }
    if (state === 'reading' && was !== 'reading' && ours) pencil.current?.focus();
    return undefined;
  }, [state]);

  const inField = (target: EventTarget) =>
    target instanceof Node && group.current !== null && group.current.contains(target);

  const onKeyDownCapture = (event: React.KeyboardEvent) => {
    if (!editing || event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
    if (!inField(event.target)) return;
    event.preventDefault();
    event.stopPropagation();
    void save();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!editing || event.defaultPrevented || !inField(event.target)) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      cancel();
      return;
    }
    if (
      event.key === 'Enter' &&
      !multiline &&
      !event.shiftKey &&
      !event.altKey &&
      !event.nativeEvent.isComposing &&
      isOneLineInput(event.target)
    ) {
      event.preventDefault();
      void save();
    }
  };

  return (
    <div
      ref={group}
      role="group"
      aria-label={named}
      data-field={fieldKey}
      data-state={state}
      data-pending={saving || undefined}
      data-readonly={readOnly ? true : undefined}
      {...(state !== 'reading' && SPLIT_HOLD)}
      className={cn(
        'grid min-w-0 gap-1.5 rounded-sm',
        saving &&
          'bg-[color-mix(in_srgb,var(--color-yellow)_22%,white)] pl-1.5 shadow-[inset_3px_0_0_var(--veritant-yellow-edge)]'
      )}
      onKeyDownCapture={onKeyDownCapture}
      onKeyDown={onKeyDown}
    >
      {state === 'reading' ? (
        <>
          <div className="flex min-w-0 items-start gap-1">
            <div className="min-w-0 flex-1 wrap-anywhere">{display}</div>
            {actions}
            {!readOnly && (
              <Button
                ref={pencil}
                type="button"
                variant="ghost"
                size="icon"
                className="-my-1 size-7 shrink-0"
                aria-label={`Edit ${named}`}
                disabled={notPermitted !== undefined}
                aria-describedby={notPermitted}
                onClick={start}
              >
                <Pencil className="size-4" aria-hidden />
              </Button>
            )}
          </div>
          {readOnly && <p className="text-xs text-muted-foreground">{readOnly.reason}</p>}
        </>
      ) : (
        <>
          <div ref={control} className="min-w-0">
            {renderControl({ label: named, value: draft, onChange: setDraft, disabled: saving })}
          </div>
          {notice && <p className="text-sm text-muted-foreground">{notice}</p>}
          {refusal && (
            <p role="alert" className="text-sm text-destructive">
              {refusal}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" disabled={saving} onClick={() => void save()}>
              Save
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={saving} onClick={cancel}>
              Cancel
            </Button>
            {saving && <span className="text-sm text-muted-foreground">Saving…</span>}
          </div>
        </>
      )}
    </div>
  );
}

/** Where a control takes focus: a text box, an editable surface, a list's trigger, a tick — never
 *  a hidden input a tick carries for its form. */
const FOCUSABLE = [
  'input:not([type="hidden"]):not([aria-hidden="true"]):not([tabindex="-1"])',
  'textarea',
  '[contenteditable="true"]',
  '[role="combobox"]',
  '[role="checkbox"]'
].join(', ');

/** Focuses the control now, or once it is drawn — the editor arrives in a chunk of its own. The
 *  returned cleanup stops waiting. */
function focusWhenDrawn(container: HTMLElement): () => void {
  const attempt = () => {
    const target = container.querySelector<HTMLElement>(FOCUSABLE);
    if (!target) return false;
    target.focus();
    return true;
  };
  if (attempt()) return () => undefined;
  const observer = new MutationObserver(() => {
    if (attempt()) observer.disconnect();
  });
  observer.observe(container, { childList: true, subtree: true, attributes: true });
  return () => observer.disconnect();
}

/** An input whose Enter has no work of its own — not a tick, a button or a file. */
function isOneLineInput(target: EventTarget): boolean {
  return (
    target instanceof HTMLInputElement &&
    !['checkbox', 'radio', 'button', 'submit', 'reset', 'file'].includes(target.type)
  );
}
